import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  Home,
  UserCheck,
  Clock,
  CheckCircle,
  XCircle,
  Ban,
  Play,
  KeyRound,
  LogOut,
  RefreshCw,
  Lock,
  Pencil,
  Trash2,
  AlertTriangle,
  Database,
  Zap,
  Inbox,
  Mail,
  Phone,
  MessageSquare,
  Check
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

import { formatDate } from '../lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_d23d8491-b3b1-4bd6-8f0e-4d1750125554/artifacts/uxopwla6_WhatsApp%20Image%202026-03-10%20at%204.20.57%20PM.jpeg";

export const SuperAdminDashboard = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Dialog states
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, request: null });
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, org: null });
  const [changeMyPasswordDialog, setChangeMyPasswordDialog] = useState(false);
  const [editOrgDialog, setEditOrgDialog] = useState({ open: false, org: null });
  const [deleteOrgDialog, setDeleteOrgDialog] = useState({ open: false, org: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [optimizeDialog, setOptimizeDialog] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null);
  
  // Form states
  const [adminPassword, setAdminPassword] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newMyPassword, setNewMyPassword] = useState('');
  const [confirmMyPassword, setConfirmMyPassword] = useState('');
  const [editOrgForm, setEditOrgForm] = useState({
    name: '',
    owner_name: '',
    email: '',
    city: '',
    capacity: 0
  });

  const fetchData = useCallback(async () => {
    if (!isSuperAdmin()) {
      navigate('/login');
      return;
    }
    
    setRefreshing(true);
    try {
      const [statsRes, requestsRes, orgsRes, enquiriesRes] = await Promise.all([
        axios.get(`${API}/organisations/stats/dashboard`),
        axios.get(`${API}/organisations/requests`),
        axios.get(`${API}/organisations/`),
        axios.get(`${API}/enquiries`)
      ]);
      
      setStats(statsRes.data);
      setRequests(requestsRes.data);
      setOrganisations(orgsRes.data);
      setEnquiries(enquiriesRes.data);
    } catch (err) {
      toast.error('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isSuperAdmin, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async () => {
    if (!adminPassword || adminPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/organisations/requests/approve`, {
        request_id: approveDialog.request.id,
        admin_password: adminPassword
      });
      
      toast.success(`Organisation "${approveDialog.request.organisation_name}" approved!`);
      setApproveDialog({ open: false, request: null });
      setAdminPassword('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      await axios.post(`${API}/organisations/requests/reject`, {
        request_id: rejectDialog.request.id,
        reason: rejectReason
      });
      
      toast.success('Request rejected');
      setRejectDialog({ open: false, request: null });
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject request');
    }
  };

  const handleEnquiryStatus = async (enquiryId, status) => {
    try {
      await axios.patch(`${API}/enquiries/${enquiryId}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.id === enquiryId ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update enquiry');
    }
  };

  const handleDeleteEnquiry = async (enquiryId) => {
    if (!window.confirm('Delete this enquiry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/enquiries/${enquiryId}`);
      setEnquiries((prev) => prev.filter((e) => e.id !== enquiryId));
      toast.success('Enquiry deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete enquiry');
    }
  };

  const handleSuspend = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to suspend "${orgName}"? All users will be blocked from logging in.`)) {
      return;
    }
    
    try {
      await axios.post(`${API}/organisations/${orgId}/suspend`);
      toast.success(`Organisation "${orgName}" suspended`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to suspend organisation');
    }
  };

  const handleActivate = async (orgId, orgName) => {
    try {
      await axios.post(`${API}/organisations/${orgId}/activate`);
      toast.success(`Organisation "${orgName}" activated`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate organisation');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/organisations/${resetPasswordDialog.org.id}/reset-admin-password`, {
        organisation_id: resetPasswordDialog.org.id,
        new_password: newPassword
      });
      
      toast.success('Admin password reset successfully');
      setResetPasswordDialog({ open: false, org: null });
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openEditOrgDialog = (org) => {
    setEditOrgForm({
      name: org.name || '',
      owner_name: org.owner_name || '',
      email: org.email || '',
      city: org.city || '',
      capacity: org.capacity || 0
    });
    setEditOrgDialog({ open: true, org });
  };

  const handleEditOrg = async () => {
    if (!editOrgForm.name.trim()) {
      toast.error('Organisation name is required');
      return;
    }
    if (!editOrgForm.owner_name.trim()) {
      toast.error('Owner name is required');
      return;
    }
    if (!editOrgForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    try {
      await axios.put(`${API}/organisations/${editOrgDialog.org.id}`, {
        name: editOrgForm.name.toUpperCase(),
        owner_name: editOrgForm.owner_name.toUpperCase(),
        email: editOrgForm.email.toLowerCase(),
        city: editOrgForm.city.toUpperCase(),
        capacity: parseInt(editOrgForm.capacity) || 0
      });
      
      toast.success('Organisation updated successfully');
      setEditOrgDialog({ open: false, org: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update organisation');
    }
  };

  const handleChangeMyPassword = async () => {
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!newMyPassword || newMyPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newMyPassword !== confirmMyPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newMyPassword
      });
      
      toast.success('Password changed successfully');
      setChangeMyPasswordDialog(false);
      setCurrentPassword('');
      setNewMyPassword('');
      setConfirmMyPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleDeleteOrg = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/organisations/${deleteOrgDialog.org.id}`);
      toast.success(`Organisation "${deleteOrgDialog.org.name}" permanently deleted`);
      setDeleteOrgDialog({ open: false, org: null });
      setDeleteConfirmText('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete organisation');
    } finally {
      setDeleting(false);
    }
  };

  const handleOptimizeDatabase = async () => {
    setOptimizing(true);
    setOptimizeResult(null);
    try {
      const response = await axios.post(`${API}/admin/create-indexes`);
      setOptimizeResult(response.data);
      toast.success('Database optimized successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to optimize database');
      setOptimizeResult({ error: err.response?.data?.detail || 'Failed to optimize database' });
    } finally {
      setOptimizing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800">Suspended</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getEnquiryBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge className="bg-blue-100 text-blue-800">New</Badge>;
      case 'contacted':
        return <Badge className="bg-amber-100 text-amber-800">Contacted</Badge>;
      case 'closed':
        return <Badge className="bg-green-100 text-green-800">Closed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const newEnquiries = enquiries.filter(e => e.status === 'new');

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="super-admin-dashboard">
      {/* Header */}
      <header className="relative bg-zinc-950 text-white shadow-xl overflow-hidden">
        <div className="absolute -top-20 right-1/4 w-72 h-72 rounded-full bg-[#002FA7]/40 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:40px_40px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="StayPoint" className="w-11 h-11 rounded-xl object-contain bg-white p-0.5 ring-1 ring-white/20" />
              <div>
                <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
                  StayPoint
                  <span className="text-[10px] uppercase tracking-[0.16em] font-semibold bg-[#002FA7] text-white px-2 py-0.5 rounded-full">Super Admin</span>
                </h1>
                <p className="text-xs text-white/50">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setOptimizeDialog(true)}
                className="text-slate-300 hover:text-white hover:bg-white/10"
                data-testid="optimize-db-btn"
              >
                <Zap className="h-4 w-4 mr-2" />
                Optimize DB
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchData}
                disabled={refreshing}
                className="text-slate-300 hover:text-white hover:bg-white/10"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setChangeMyPasswordDialog(true)}
                className="text-slate-300 hover:text-white hover:bg-white/10"
                data-testid="change-password-btn"
              >
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-slate-300 hover:text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Organisations', value: stats?.total_organisations || 0, icon: Building2, grad: 'from-blue-500 to-blue-700', ring: 'ring-blue-100' },
            { label: 'Active Organisations', value: stats?.active_organisations || 0, icon: CheckCircle, grad: 'from-emerald-500 to-green-700', ring: 'ring-emerald-100' },
            { label: 'Pending Requests', value: stats?.pending_requests || 0, icon: Clock, grad: 'from-amber-500 to-orange-600', ring: 'ring-amber-100' },
            { label: 'Total Guests', value: stats?.total_guests || 0, icon: UserCheck, grad: 'from-violet-500 to-purple-700', ring: 'ring-violet-100' },
          ].map((stat, idx) => (
            <div
              key={stat.label}
              className={`dash-animate relative overflow-hidden rounded-2xl bg-white border border-slate-200 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ${stat.ring}`}
              style={{ animationDelay: `${idx * 60}ms` }}
              data-testid={`superadmin-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${stat.grad} opacity-10`} />
              <div className="relative flex items-start justify-between">
                <div className="min-w-0">
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wide leading-tight">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1.5 leading-none">{stat.value}</p>
                </div>
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${stat.grad} flex items-center justify-center shrink-0 shadow-sm`}>
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <Tabs defaultValue="requests" className="space-y-6">
          <TabsList className="grid w-full max-w-xl grid-cols-3">
            <TabsTrigger value="requests" className="relative">
              Requests
              {pendingRequests.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingRequests.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="enquiries" className="relative" data-testid="enquiries-tab">
              Enquiries
              {newEnquiries.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {newEnquiries.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="organisations">Organisations</TabsTrigger>
          </TabsList>

          {/* Requests Tab */}
          <TabsContent value="requests">
            <Card className="dash-animate border-slate-200 shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle>Organisation Requests</CardTitle>
                <CardDescription>Review and manage access requests from new organisations</CardDescription>
              </CardHeader>
              <CardContent>
                {requests.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No requests yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Organisation</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Capacity</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell className="font-medium">{request.organisation_name}</TableCell>
                          <TableCell>{request.owner_name}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{request.email}</div>
                              <div className="text-slate-500">{request.mobile}</div>
                            </div>
                          </TableCell>
                          <TableCell>{request.city}</TableCell>
                          <TableCell>{request.capacity} beds</TableCell>
                          <TableCell>{getStatusBadge(request.status)}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDate(request.created_at)}
                          </TableCell>
                          <TableCell>
                            {request.status === 'pending' ? (
                              <div className="flex space-x-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => setApproveDialog({ open: true, request })}
                                  data-testid={`approve-${request.id}`}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => setRejectDialog({ open: true, request })}
                                  data-testid={`reject-${request.id}`}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            ) : (
                              <span className="text-sm text-slate-500">
                                {request.rejection_reason && `Reason: ${request.rejection_reason}`}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries">
            <Card className="dash-animate border-slate-200 shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Inbox className="h-5 w-5 text-blue-600" /> Demo / Contact Enquiries
                </CardTitle>
                <CardDescription>Leads from the website contact &amp; request-a-demo form. Mark them as contacted or closed to track follow-ups.</CardDescription>
              </CardHeader>
              <CardContent>
                {enquiries.length === 0 ? (
                  <div className="text-center py-12" data-testid="enquiries-empty">
                    <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No enquiries yet. New website leads will appear here.</p>
                  </div>
                ) : (
                  <Table data-testid="enquiries-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>PG / Organisation</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead className="max-w-xs">Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enquiries.map((enq) => (
                        <TableRow key={enq.id} data-testid={`enquiry-row-${enq.id}`}>
                          <TableCell className="font-medium">{enq.name}</TableCell>
                          <TableCell>{enq.org_name || <span className="text-slate-400">—</span>}</TableCell>
                          <TableCell>
                            <div className="text-sm space-y-1">
                              <a href={`mailto:${enq.email}`} className="flex items-center gap-1.5 text-blue-700 hover:underline">
                                <Mail className="h-3.5 w-3.5" /> {enq.email}
                              </a>
                              <a href={`tel:${enq.phone}`} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700">
                                <Phone className="h-3.5 w-3.5" /> {enq.phone}
                              </a>
                            </div>
                          </TableCell>
                          <TableCell>{enq.city || <span className="text-slate-400">—</span>}</TableCell>
                          <TableCell className="max-w-xs">
                            {enq.message ? (
                              <span className="text-sm text-slate-600 line-clamp-2" title={enq.message}>{enq.message}</span>
                            ) : <span className="text-slate-400">—</span>}
                          </TableCell>
                          <TableCell>{getEnquiryBadge(enq.status)}</TableCell>
                          <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                            {formatDate(enq.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              {enq.status !== 'contacted' && enq.status !== 'closed' && (
                                <Button
                                  size="sm" variant="outline"
                                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                  onClick={() => handleEnquiryStatus(enq.id, 'contacted')}
                                  data-testid={`enquiry-contacted-${enq.id}`}
                                >
                                  <MessageSquare className="h-4 w-4 mr-1" /> Contacted
                                </Button>
                              )}
                              {enq.status !== 'closed' && (
                                <Button
                                  size="sm" variant="outline"
                                  className="border-green-300 text-green-700 hover:bg-green-50"
                                  onClick={() => handleEnquiryStatus(enq.id, 'closed')}
                                  data-testid={`enquiry-closed-${enq.id}`}
                                >
                                  <Check className="h-4 w-4 mr-1" /> Close
                                </Button>
                              )}
                              <Button
                                size="sm" variant="ghost"
                                className="text-slate-400 hover:text-red-600"
                                onClick={() => handleDeleteEnquiry(enq.id)}
                                data-testid={`enquiry-delete-${enq.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organisations Tab */}
          <TabsContent value="organisations">
            <Card className="dash-animate border-slate-200 shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle>All Organisations</CardTitle>
                <CardDescription>Manage existing organisations and their status</CardDescription>
              </CardHeader>
              <CardContent>
                {organisations.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No organisations yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Organisation</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead>Guests (Active / Capacity)</TableHead>
                        <TableHead>Properties</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {organisations.map((org) => (
                        <TableRow key={org.id}>
                          <TableCell className="font-medium">{org.name}</TableCell>
                          <TableCell>{org.owner_name}</TableCell>
                          <TableCell>{org.email}</TableCell>
                          <TableCell>{org.city}</TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <Users className="h-4 w-4 mr-1 text-slate-400" />
                              {org.user_count || 0}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const active = org.active_guest_count ?? 0;
                              const cap = org.capacity || 0;
                              const full = cap > 0 && active >= cap;
                              return (
                                <div className={`flex items-center font-medium ${full ? 'text-rose-600' : 'text-slate-700'}`} data-testid={`org-guests-${org.id}`}>
                                  <UserCheck className={`h-4 w-4 mr-1 ${full ? 'text-rose-500' : 'text-slate-400'}`} />
                                  {active} / {cap || '—'}
                                  {full && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">FULL</span>}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <Home className="h-4 w-4 mr-1 text-slate-400" />
                              {org.property_count || 0}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(org.status)}</TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              {org.status === 'active' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleSuspend(org.id, org.name)}
                                  data-testid={`suspend-${org.id}`}
                                >
                                  <Ban className="h-4 w-4 mr-1" />
                                  Suspend
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => handleActivate(org.id, org.name)}
                                  data-testid={`activate-${org.id}`}
                                >
                                  <Play className="h-4 w-4 mr-1" />
                                  Activate
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => openEditOrgDialog(org)}
                                data-testid={`edit-${org.id}`}
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setResetPasswordDialog({ open: true, org })}
                                data-testid={`reset-pwd-${org.id}`}
                              >
                                <KeyRound className="h-4 w-4 mr-1" />
                                Reset Password
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => setDeleteOrgDialog({ open: true, org })}
                                data-testid={`delete-${org.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onOpenChange={(open) => setApproveDialog({ open, request: approveDialog.request })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Organisation</DialogTitle>
            <DialogDescription>
              Approving "{approveDialog.request?.organisation_name}" will create their account with the email{' '}
              <strong>{approveDialog.request?.email}</strong>. Set their admin password below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-password">Admin Password</Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="Set password for org admin"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                data-testid="admin-password-input"
              />
              <p className="text-xs text-slate-500">Minimum 6 characters. Share this with the organisation owner.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog({ open: false, request: null })}>
              Cancel
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApprove}>
              Approve & Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, request: rejectDialog.request })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Request</DialogTitle>
            <DialogDescription>
              Rejecting "{rejectDialog.request?.organisation_name}". Please provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Reason for Rejection</Label>
              <Input
                id="reject-reason"
                type="text"
                placeholder="e.g., Incomplete information, duplicate request"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, request: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => setResetPasswordDialog({ open, org: resetPasswordDialog.org })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Admin Password</DialogTitle>
            <DialogDescription>
              Reset the admin password for "{resetPasswordDialog.org?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-500">Minimum 6 characters</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialog({ open: false, org: null })}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword}>
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change My Password Dialog */}
      <Dialog open={changeMyPasswordDialog} onOpenChange={setChangeMyPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Your Password</DialogTitle>
            <DialogDescription>
              Update your Super Admin account password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-my-password">New Password</Label>
              <Input
                id="new-my-password"
                type="password"
                placeholder="Enter new password"
                value={newMyPassword}
                onChange={(e) => setNewMyPassword(e.target.value)}
                data-testid="new-my-password-input"
              />
              <p className="text-xs text-slate-500">Minimum 6 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-my-password">Confirm New Password</Label>
              <Input
                id="confirm-my-password"
                type="password"
                placeholder="Confirm new password"
                value={confirmMyPassword}
                onChange={(e) => setConfirmMyPassword(e.target.value)}
                data-testid="confirm-my-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setChangeMyPasswordDialog(false);
              setCurrentPassword('');
              setNewMyPassword('');
              setConfirmMyPassword('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleChangeMyPassword}>
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Organisation Dialog */}
      <Dialog open={editOrgDialog.open} onOpenChange={(open) => setEditOrgDialog({ open, org: editOrgDialog.org })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Organisation</DialogTitle>
            <DialogDescription>
              Update details for "{editOrgDialog.org?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-org-name">Organisation Name *</Label>
              <Input
                id="edit-org-name"
                type="text"
                value={editOrgForm.name}
                onChange={(e) => setEditOrgForm({ ...editOrgForm, name: e.target.value.toUpperCase() })}
                data-testid="edit-org-name-input"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-owner-name">Owner Name *</Label>
              <Input
                id="edit-owner-name"
                type="text"
                value={editOrgForm.owner_name}
                onChange={(e) => setEditOrgForm({ ...editOrgForm, owner_name: e.target.value.toUpperCase() })}
                data-testid="edit-owner-name-input"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={editOrgForm.email}
                onChange={(e) => setEditOrgForm({ ...editOrgForm, email: e.target.value.toLowerCase() })}
                data-testid="edit-email-input"
                style={{ textTransform: 'lowercase' }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  type="text"
                  value={editOrgForm.city}
                  onChange={(e) => setEditOrgForm({ ...editOrgForm, city: e.target.value.toUpperCase() })}
                  data-testid="edit-city-input"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-capacity">Capacity (Beds)</Label>
                <Input
                  id="edit-capacity"
                  type="number"
                  value={editOrgForm.capacity}
                  onChange={(e) => setEditOrgForm({ ...editOrgForm, capacity: e.target.value })}
                  data-testid="edit-capacity-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOrgDialog({ open: false, org: null })}>
              Cancel
            </Button>
            <Button onClick={handleEditOrg}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Organisation Dialog */}
      <Dialog open={deleteOrgDialog.open} onOpenChange={(open) => {
        if (!open) {
          setDeleteOrgDialog({ open: false, org: null });
          setDeleteConfirmText('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Organisation Permanently
            </DialogTitle>
            <DialogDescription className="pt-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="font-semibold text-red-800 mb-2">Warning: This action cannot be undone!</p>
                <p className="text-red-700 text-sm">
                  Deleting <strong>"{deleteOrgDialog.org?.name}"</strong> will permanently remove:
                </p>
                <ul className="list-disc list-inside text-red-700 text-sm mt-2 space-y-1">
                  <li>All users associated with this organisation</li>
                  <li>All guests and their records</li>
                  <li>All properties and rooms</li>
                  <li>All payments and transactions</li>
                  <li>All activity logs and other data</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="delete-confirm">
                Type <span className="font-mono font-bold bg-slate-100 px-2 py-1 rounded">DELETE</span> to confirm
              </Label>
              <Input
                id="delete-confirm"
                type="text"
                placeholder="Type DELETE"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                data-testid="delete-confirm-input"
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteOrgDialog({ open: false, org: null });
                setDeleteConfirmText('');
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteOrg}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Optimize Database Dialog */}
      <Dialog open={optimizeDialog} onOpenChange={(open) => {
        if (!open && !optimizing) {
          setOptimizeDialog(false);
          setOptimizeResult(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Database className="h-5 w-5 mr-2 text-blue-600" />
              Optimize Database Performance
            </DialogTitle>
            <DialogDescription className="pt-2">
              {!optimizeResult ? (
                <div className="space-y-3">
                  <p>This will create database indexes to speed up all queries.</p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="font-semibold text-blue-800 mb-2">What this does:</p>
                    <ul className="list-disc list-inside text-blue-700 text-sm space-y-1">
                      <li>Creates lookup indexes for faster data retrieval</li>
                      <li>Speeds up Guest, Payment, Property searches by 10-50x</li>
                      <li>Does NOT modify or delete any data</li>
                      <li>Safe to run multiple times (skips existing indexes)</li>
                    </ul>
                  </div>
                  <p className="text-sm text-slate-500">
                    Run this once after deployment. Takes about 5-30 seconds depending on data size.
                  </p>
                </div>
              ) : optimizeResult.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="font-semibold text-red-800">Error:</p>
                  <p className="text-red-700 text-sm">{optimizeResult.error}</p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-semibold text-green-800 mb-2 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Optimization Complete!
                  </p>
                  <p className="text-green-700 text-sm mb-2">
                    Created <strong>{optimizeResult.total_indexes}</strong> indexes across all collections.
                  </p>
                  <div className="text-xs text-green-600 space-y-1">
                    {optimizeResult.details?.indexes_created?.map((item, idx) => (
                      <div key={idx}>✓ {item}</div>
                    ))}
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {!optimizeResult ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setOptimizeDialog(false)}
                  disabled={optimizing}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleOptimizeDatabase}
                  disabled={optimizing}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="run-optimize-btn"
                >
                  {optimizing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Optimizing...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Run Optimization
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => {
                setOptimizeDialog(false);
                setOptimizeResult(null);
              }}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
